import { Injectable, inject } from '@angular/core'
import {
  ElderToastService,
  HttpParamsBuilder,
  Pageable,
  RestClientPaged,
} from '@elderbyte/ngx-starter'
import { HttpClient } from '@angular/common/http'
import { environment } from '../../../../environments/environment'
import { LoggerFactory } from '@elderbyte/ts-logger'
import {
  NewWorkOrder,
  WorkOrder,
  WorkOrderOverview,
  WorkOrderStorageLocationOverview,
} from './work-order'
import { Observable, of, PartialObserver } from 'rxjs'
import { map, switchMap } from 'rxjs/operators'
import { WorkOrderUpdate } from './work-order-update'
import { WorkOrderFilterBuilder } from './work-order-filter-builder'
import { WorkOrderCommand } from './commands/WorkOrderCommand'
import {
  MultiWorkorderCommand,
  MultiWorkOrderCommandType,
} from './commands/multi-work-order-command'

export interface WorkOrderCommandResult {
  success: boolean
  message: string
}

@Injectable({
  providedIn: 'root',
})
export class WorkOrderService extends RestClientPaged<WorkOrder, string> {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly log = LoggerFactory.getLogger(this.constructor.name)

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {
    const http = inject(HttpClient)

    super(environment.apiPath + '/work-orders', http, {
      idField: 'id',
    })
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  // TODO move to backend
  public findOrCreateEmptyWorkOrder(newWorkOrder: NewWorkOrder): Observable<WorkOrder> {
    return this.findAllPaged(
      new Pageable(0, 100),
      WorkOrderFilterBuilder.start()
        .prodOrders(newWorkOrder.prodOrderId)
        .manufactureFacility(newWorkOrder.manufactureFacility)
        .collectingFacility(newWorkOrder.collectingFacility)
        .build()
    ).pipe(
      switchMap((page) => {
        const wos = page.content
        if (wos && wos.some((w) => w.isEmpty)) {
          return of(wos.find((w) => w.isEmpty))
        } else {
          return this.create(newWorkOrder)
        }
      })
    )
  }

  /**
   * Find all WorkOrders for the given ProdOrder
   * @param prodOrderId
   */
  public findWorkOrders(prodOrderId: string): Observable<WorkOrder[]> {
    return this.findAllPaged(new Pageable(0, 100), [
      WorkOrderFilterBuilder.prodOrders(prodOrderId),
    ]).pipe(map((page) => this.sortByFacilitySignature(page.content)))
  }

  public getOverview(orderId: string): Observable<WorkOrderOverview> {
    return this.http.get<WorkOrderOverview>(this.getEntityUrl(orderId) + '/overview')
  }

  public getStorageOverview(
    orderId: string,
    batchId: string
  ): Observable<WorkOrderStorageLocationOverview> {
    const params = HttpParamsBuilder.start().append('batchId', batchId).build()

    return this.http.get<WorkOrderStorageLocationOverview>(
      this.getEntityUrl(orderId) + '/storage-overview',
      { params: params }
    )
  }

  public patch(patch: WorkOrderUpdate): Observable<WorkOrder> {
    return super.patchInternal(patch.id, patch)
  }

  public executeCommand(
    orderId: string,
    command: WorkOrderCommand
  ): Observable<WorkOrderCommandResult> {
    return this.http.post<WorkOrderCommandResult>(`${this.getEntityUrl(orderId)}/commands`, command)
  }

  public executeMultiCommand(command: MultiWorkorderCommand): Observable<WorkOrderCommandResult> {
    return this.http.post<WorkOrderCommandResult>(`${this.restEndpoint}/commands`, command)
  }

  public commandSubscriber(
    toastService: ElderToastService
  ): PartialObserver<WorkOrderCommandResult> {
    return {
      next: (result) => {
        if (result && result.success) {
          if (result.message) {
            toastService.info(result.message)
          } else {
            toastService.info('messages.action_successful')
          }
        } else {
          toastService.error('messages.action_failed', result.message)
        }
      },
      error: (err) => toastService.error('messages.action_failed', err),
    }
  }

  /***************************************************************************
   *                                                                         *
   * Private Methods                                                         *
   *                                                                         *
   **************************************************************************/

  /**
   * example result:
   *
   * wo1: aig/aig
   * wo2: aig/hei
   * wo3: obe/obe
   * wo4: obe/esc
   * wo5: obe/hei
   */
  private sortByFacilitySignature(workOrders: WorkOrder[]): WorkOrder[] {
    return workOrders.sort((wo1, wo2) => {
      if (wo1.isEmpty && wo2.isEmpty) {
        if (wo1.manufactureFacility === wo2.manufactureFacility) {
          if (
            wo1.manufactureFacility === wo1.collectingFacility &&
            wo2.manufactureFacility === wo2.collectingFacility
          ) {
            return wo1.createdAt > wo2.createdAt ? 1 : -1
          } else if (wo1.manufactureFacility === wo1.collectingFacility) {
            return -1
          } else if (wo2.manufactureFacility === wo2.collectingFacility) {
            return 1
          } else {
            return wo1.collectingFacility.localeCompare(wo2.collectingFacility)
          }
        } else {
          return wo1.manufactureFacility.localeCompare(wo2.manufactureFacility)
        }
      } else {
        return wo1.isEmpty ? 1 : 0
      }
    })
  }

  findLabels(facility: string): Observable<string[]> {
    return this.http.get<string[]>(`${this.restEndpoint}/labels`, {
      params: { facility },
    })
  }
}
